import { useMemo, useState } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveSidePanelModal } from "panels/content/side-panel/slice"
import {
    LiveVideoStatus,
    selectLiveAtlasLiveVideoFlights,
    selectLiveLiveVideoFlights,
    selectLiveVideoFlightById,
    selectLiveVideoFlightIdsOrderedByDateCreated,
    selectLiveVideoFlights,
    selectLiveVideoStatus,
    selectNonLiveLiveVideoFlights,
    selectStickyLiveVideoFlights,
} from "features/entity-caching/flights/selectors"
import {
    createLiveVideoFlight,
    deleteLiveVideoFlight,
    updateLiveVideoFlight,
} from "features/pubsub/entityActions/flights/liveVideo"
import { ModalNames } from "features/modals/types"
import { LayerPluginSpec, PluginRegistry, PluginSpec } from "plugins/plugin-registry"
import * as Icons from "@nova/component-icons"
import { LiveVideoFlight } from "@nova/protobuf"
import {
    AddOns,
    SidePanelLayerOrGroup,
    SidePanelLayerType,
    SupportedRegions,
} from "@nova/constants"
import {
    removeLayerFromSelection,
    selectRenamingLayerId,
    selectSelectedLayerIds,
    setRenamingLayerId,
} from "panels/content/side-panel/slice.new"
import classNames from "classnames"
import { RenamableLabel } from "@nova/component-renamable-label"
import { useFullnameForUserId } from "features/data-management/users/storage"
import { useTimeElapsed } from "@nova/component-time-elapsed"
import { getUseSidePanelEntities } from "panels/content/side-panel/generics/genericUseSidePanelEntities"
import { getGenericAssetsListItem } from "panels/content/side-panel/generics/genericAssetsListItems"
import { LiveVideoAssetsThumbnail } from "panels/content/side-panel/assets-thumbnails/LiveVideoAssetsThumbnail"
import { getGenericLayerListItem } from "panels/content/side-panel/generics/GenericLayerListItem"
import store from "integrations/redux/store"
import { RootState } from "integrations/redux"
import { LiveVideoDataModal } from "./modals/LiveVideoDataModal"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { ContextMenuItem } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { useMoveActions } from "panels/content/side-panel/generics/genericContextMenuSections"
import { useArchiveContextMenuOption } from "panels/content/side-panel/generics/useArchiveContextMenuOption"
import { useIsEditor } from "tools/hooks/useIsEditor"
import { getGenericLayerDataInspectorPanelComponent } from "features/map/inspector-panel/inspector-panels/GenericInspectorPanel"
import { InspectorPanelLiveVideoLayer } from "features/map/inspector-panel/live-videos/InspectorPanelLiveVideoLayer"
import { InspectorPanelLiveVideoData } from "features/map/inspector-panel/live-videos/InspectorPanelLiveVideoData"
import { VideoFlightPlayer } from "../ui/VideoContainer"
import { useIsStreamDisconnected } from "../controllers/hooks/useControllerEvents"
import { toggleVideoFlight } from "../state/thunks"
import { selectIsVideoFlightOpen } from "../state/selectors"
import { activateSidePanelModal } from "panels/content/side-panel/thunks"
import { InspectorPanelPlugin } from "features/map/inspector-panel/useInspectorPanel"
import { getUseVideoFlightPath } from "../shared/useVideoFlightPath"
import { ExpandableContent } from "@nova/component-expandable-content"
import { getVideoLayerIconComponent } from "../FMV/VideoPlugin"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { showGlobalModal } from "runlevel/actions"
import { getUseLiveVideoMapNodes } from "features/map/map-nodes/useLiveVideoMapNodes"
import { getUseSidePanelListItemVisibilityAndLockActions } from "panels/content/side-panel/generics/genericListItemActions"
import { droneIconColors } from "features/map/layers/video/VideoDroneLayer"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { UserProfileModalTrigger } from "features/users/UserProfileModal"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import {
    selectActiveWorkspaceId,
    selectWorkspaceRegion,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { setGlobalNotificationContext } from "user/profile/slice"
import {
    getGenericFlyoutLayerListItem,
    useLiveAtlasLiveVideoActions,
} from "plugins/UserLocationShare/ui/UserLocationFlyoutListItem"
import { getApiGatewayDomainForRegion } from "@nova/configuration/browser"

export function registerLiveVideoPlugin(registry: PluginRegistry) {
    registry
        .register<LiveVideoFlight>({
            name: "liveVideo",
            displayName: "Live Stream",
            sidePanelLayerType: SidePanelLayerType.LiveVideoFlight,
            PrimaryIcon: Icons.LiveVideoPlugin,
            ImageIconUrl: "/images/plugins/layers/live-video.svg",
            updateAction: updateLiveVideoFlight,
            deleteAction: deleteLiveVideoFlight,
            confirmDelete: () => true,
            protobufMessage: LiveVideoFlight,
            selectAllEntities: selectLiveVideoFlights,
            selectSingleEntity: selectLiveVideoFlightById,
            zoomTo: () => undefined,
            duplicate: () => undefined,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin", (spec) => ({
            useSidePanelEntities: getUseSidePanelEntities({
                protobufMessage: spec.protobufMessage,
                selectSingleEntity: spec.selectSingleEntity,
                selectAllEntities: selectNonLiveLiveVideoFlights,
            }),
            SidePanelListItem: getLiveVideoListItem(spec),
            useContextMenuSections: getUseLiveVideoContextMenuSections({
                spec,
                includeMoveAndArchiveActions: true,
            }),
        }))
        .implement("side-panel-assets-plugin", (spec) => ({
            useSidePanelEntities: getUseSidePanelEntities({
                protobufMessage: spec.protobufMessage,
                selectSingleEntity: spec.selectSingleEntity,
                selectAllEntities: selectNonLiveLiveVideoFlights,
            }),
            SidePanelListItem: getGenericAssetsListItem({
                spec,
                ThumbnailRender: LiveVideoAssetsThumbnail,
                onClick: ({ id }) => {
                    store.dispatch(toggleVideoFlight({ flightId: id }))
                },
            }),
            useContextMenuSections: getUseLiveVideoContextMenuSections({
                spec,
            }),
        }))
        .implement("top-menu-flyout-layers-plugin", (spec) => ({
            flyoutId: "atlas",
            useEntities: getUseSidePanelEntities({
                protobufMessage: spec.protobufMessage,
                selectSingleEntity: spec.selectSingleEntity,
                selectAllEntities: selectLiveAtlasLiveVideoFlights,
            }),
            LayerListItem: getGenericFlyoutLayerListItem({
                showLiveBadge: true,
                useActions: useLiveAtlasLiveVideoActions,
                spec,
            }),
        }))
        .implement("side-panel-sticky-layers-plugin", (spec) => ({
            useSidePanelEntities: getUseSidePanelEntities({
                protobufMessage: spec.protobufMessage,
                selectSingleEntity: spec.selectSingleEntity,
                selectAllEntities: selectStickyLiveVideoFlights,
            }),
            SidePanelListItem: getLiveVideoStickyListItem(spec),
            useContextMenuSections: getUseLiveVideoContextMenuSections({
                spec,
            }),
        }))
        .implement("side-panel-assets-plugin", (spec) => ({
            useSidePanelEntities: getUseSidePanelEntities({
                protobufMessage: spec.protobufMessage,
                selectSingleEntity: spec.selectSingleEntity,
                selectAllEntities: selectLiveLiveVideoFlights,
            }),
            SidePanelListItem: getGenericAssetsListItem({
                spec,
                ThumbnailRender: LiveVideoAssetsThumbnail,
                onClick: ({ id }) => {
                    store.dispatch(toggleVideoFlight({ flightId: id }))
                },
            }),
            useContextMenuSections: getUseLiveVideoContextMenuSections({ spec }),
            useThisCategoryHeader: false, // Bundled with non-live live videos above
        }))
        .implement("map-layer-plugin", (spec) => ({
            useLayer: getUseVideoFlightPath(spec),
        }))
        .implement("feature-plugin", () => ({
            subscriptionType: AddOns.LiveVideo,
            createAction: ({ data: { createdBy } }) =>
                createLiveVideoFlight({ data: { createdBy, name: "", saveRecording: true } }),
            onClick: () => {
                store.dispatch(
                    initializeCenterModalForComponentAction(ModalNames.ChooseLiveVideoTypeModal)
                )
                store.dispatch(showGlobalModal())
            },
            // sampleDataModalName: ModalNames.TryLiveVideoModal,
        }))
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getLiveVideoInpsectorPanel(spec),
            useHeaderActions: () => [],
        }))
        .implement("map-node-plugin", (spec) => ({
            useMapNodes: getUseLiveVideoMapNodes(spec),
        }))
}

function getUseLiveVideoContextMenuSections({
    spec,
    includeMoveAndArchiveActions = false,
}: {
    spec: PluginSpec<LiveVideoFlight>
    includeMoveAndArchiveActions?: boolean
}) {
    const useLiveVideoLayerListContextMenuSections = (
        id: string,
        sortedEntities: SidePanelLayerOrGroup[] = []
    ) => {
        const dispatch = useAppDispatch()
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const confirmationModal = useConfirmationModalContext()
        const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
        const isEditor = useIsEditor()
        const orgId = useAppSelector(selectActiveWorkspaceId())
        const projectId = useAppSelector(selectActivatedFireId)
        const region = useAppSelector(selectWorkspaceRegion()) || SupportedRegions.Canada
        const actions = [] as ContextMenuItem[][]
        if (entity.isMMA) {
            actions.push([
                {
                    title: "Copy Link",
                    Icon: Icons.Copy,
                    onClick: () => {
                        if (!orgId || !projectId || !id) return
                        fetchApiGateway(
                            "post /org/:orgId/project/:projectId/flight/:flightId/get-mma-livestream-auth",
                            {
                                params: { orgId, projectId, flightId: id },
                                body: { saveRecording: entity.saveRecording, region },
                                region: SupportedRegions.Canada,
                            }
                        )
                            .then(({ url }) => {
                                navigator.clipboard
                                    .writeText(
                                        `-telemetry-url ${getApiGatewayDomainForRegion(SupportedRegions.Canada)}/org/${orgId}/project/${projectId}/flight/${id}/livestream-metadata ${url}`
                                    )
                                    .then(() => {
                                        dispatch(
                                            setGlobalNotificationContext({
                                                status: GlobalNotificationStatus.success,
                                                message: "Stream link copied to clipboard",
                                                timeoutInMs: 3000,
                                            })
                                        )
                                    })
                            })
                            .catch(() => {
                                dispatch(
                                    setGlobalNotificationContext({
                                        status: GlobalNotificationStatus.error,
                                        message: "Failed to copy link. Please try again.",
                                        timeoutInMs: 3000,
                                    })
                                )
                            })
                    },
                },
            ])
        }
        if (isEditor) {
            actions.push([
                {
                    title: "Rename",
                    onClick: () => {
                        dispatch(setRenamingLayerId({ id }))
                    },
                    Icon: Icons.Pencil,
                },
            ])
        }
        const moveActions = useMoveActions({
            spec: spec as unknown as LayerPluginSpec,
            id,
            mode: "Layers",
            sortedEntities,
        })
        const archiveAction = useArchiveContextMenuOption(id)
        if (includeMoveAndArchiveActions) {
            if (moveActions.length) {
                actions.push(moveActions)
            }
            if (archiveAction) {
                actions.push([archiveAction])
            }
        }
        if (isEditor) {
            actions.push([
                {
                    title: "Delete",
                    onClick: async () => {
                        if (spec.confirmDelete(entity)) {
                            const didConfirm = await confirmationModal.confirm({
                                RenderComponent: getGenericConfirmationModalComponent({
                                    title: "Delete Selection?",
                                    message: `This will delete ${entity.name}, do you want to proceed?`,
                                }),
                            })
                            if (!didConfirm) return
                        }
                        dispatch(spec.deleteAction({ id }))
                        if (selectedLayerIds.includes(id)) {
                            dispatch(removeLayerFromSelection({ id }))
                        }
                    },
                    Icon: Icons.Trash,
                    isDestructive: true,
                },
            ])
        }
        return actions
    }
    return useLiveVideoLayerListContextMenuSections
}

function getLiveVideoListItem(spec: PluginSpec<LiveVideoFlight>) {
    const LiveVideoStandbyIndicator = ({ id }: { id: string; isVisible: boolean }) => {
        const flight = useAppSelector(spec.selectSingleEntity(id))
        const { fullName } = useFullnameForUserId(flight?.createdBy?.userId)
        const status = selectLiveVideoStatus(flight)

        if (status === LiveVideoStatus.Expired)
            return <small className="text-gray-400">Expired</small>
        if (status === LiveVideoStatus.Standby)
            return (
                <>
                    <small className="text-gray-400">Standby</small>
                    {flight?.createdBy && (
                        <div className="flex gap-2 items-center">
                            <div className="w-6 min-w-[24px] aspect-square rounded-[5px] overflow-hidden">
                                <ProfilePhoto
                                    userId={flight.createdBy.userId}
                                    fullName={fullName || ""}
                                />
                            </div>
                            <small className="text-gray-200">{fullName}</small>
                        </div>
                    )}
                </>
            )

        return null
    }

    const GenericListItem = getGenericLayerListItem({
        spec,
        ProgressComponent: LiveVideoStandbyIndicator,
        useActions: getUseSidePanelListItemVisibilityAndLockActions(spec),
        LayerIconComponent: getVideoLayerIconComponent(spec),
        onClick: ({ id }) => {
            const state = store.getState() as RootState
            const flight = spec.selectSingleEntity(id)(state)
            const status = selectLiveVideoStatus(flight)

            switch (status) {
                case LiveVideoStatus.Expired:
                    break

                // Open Data Side Panel
                case LiveVideoStatus.AwaitingCodeLink:
                case LiveVideoStatus.Completed:
                    if (!flight.isMMA) {
                        store.dispatch(
                            activateSidePanelModal({
                                id,
                                type: "layer",
                                layerContentType: "live",
                            })
                        )
                    }
                    break

                // Toggle Video Flight Player
                case LiveVideoStatus.Standby:
                case LiveVideoStatus.LivePending:
                case LiveVideoStatus.Live:
                case LiveVideoStatus.AwaitingRecording:
                default:
                    store.dispatch(toggleVideoFlight({ flightId: id }))
            }
        },
    })
    const LiveVideoListItem = ({ id, isHovered }: { id: string; isHovered: boolean }) => {
        const flight = useAppSelector(spec.selectSingleEntity(id))
        const activeSidePanelModal = useAppSelector(selectActiveSidePanelModal())
        const activeSidePanelModalId = activeSidePanelModal?.id
        const active = activeSidePanelModalId === id
        const dataIsOpen =
            active && selectLiveVideoStatus(flight) === LiveVideoStatus.AwaitingCodeLink
        return (
            <>
                {dataIsOpen && <LiveVideoDataModal />}
                <GenericListItem id={id} isHovered={isHovered} />
            </>
        )
    }
    return LiveVideoListItem
}

function getLiveVideoStickyListItem(spec: PluginSpec<LiveVideoFlight>) {
    const LiveVideoStickyListItem = ({ id, isHovered }: { id: string; isHovered: boolean }) => {
        const dispatch = useAppDispatch()
        const isEditor = useIsEditor()
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const renamingLayerId = useAppSelector(selectRenamingLayerId)
        const isRenaming = renamingLayerId === id
        const isActive = useAppSelector(selectIsVideoFlightOpen(id))
        const isDisconnected = useIsStreamDisconnected({ flightId: id })
        const { fullName } = useFullnameForUserId(entity.createdBy?.userId)
        const shortName = useMemo(() => {
            if (!fullName) return undefined
            const names = fullName?.trim().split(" ")
            return names[0] + (names.at(1) ? ` ${names[1][0]}.` : "")
        }, [fullName])
        const formattedDuration = useTimeElapsed(entity.startedAt && entity.startedAt * 1000)
        const [isPreviewOpen, setIsPreviewOpen] = useState(true)
        const liveFlightIdsOrdered = useAppSelector(selectLiveVideoFlightIdsOrderedByDateCreated)
        const droneIconColor = useMemo(() => {
            const thisFlightIndex = liveFlightIdsOrdered.findIndex((f) => f.id === id)
            return droneIconColors[thisFlightIndex % droneIconColors.length]
        }, [id, liveFlightIdsOrdered])
        return (
            <div
                className="w-full h-full flex flex-col rounded-lg p-2"
                onClick={(e) => {
                    if (!e.shiftKey && !e.metaKey && !e.ctrlKey) {
                        dispatch(toggleVideoFlight({ flightId: id }))
                    }
                }}
                onDoubleClick={() => {
                    if (!isEditor) return
                    dispatch(setRenamingLayerId({ id }))
                }}
            >
                <div className="w-full h-full flex gap-2">
                    <div className="flex flex-col items-center h-full gap-1">
                        <div className="w-5 h-5 flex justify-center items-center">
                            <Icons.ExtraSmallIcon
                                className="shrink-0"
                                Icon={isActive ? Icons.PlayCircle : Icons.DroneVideoIcon}
                                iconClassName={classNames({
                                    "text-blue-500": isActive,
                                })}
                                style={
                                    isActive
                                        ? {}
                                        : {
                                              color: droneIconColor,
                                          }
                                }
                            />
                        </div>
                        <Icons.ExtraSmallIcon
                            Icon={isPreviewOpen ? Icons.CollapseAll : Icons.ExpandAll}
                            iconClassName={classNames(
                                "cursor-pointer text-gray-300 hover:text-white-1000",
                                {
                                    "opacity-0": !isHovered || isActive,
                                }
                            )}
                            onClick={(e) => {
                                e.stopPropagation()
                                setIsPreviewOpen((prev) => !prev)
                            }}
                        />
                    </div>
                    <div className="w-full flex flex-col gap-1 justify-center items-center shrink min-w-0">
                        <div className="flex justify-between w-full items-center">
                            <RenamableLabel
                                value={entity.name}
                                rename={isRenaming}
                                setRename={
                                    isRenaming
                                        ? (r) =>
                                              dispatch(
                                                  setRenamingLayerId({ id: r ? id : undefined })
                                              )
                                        : () => null
                                }
                                classNames={classNames("w-max select-none", {
                                    "shrink truncate": !isRenaming,
                                })}
                                inputClassNames="!max-h-5 !text-[12px]"
                                onRename={(name) => {
                                    dispatch(spec.updateAction({ id, updates: { name } }))
                                    dispatch(setRenamingLayerId({ id: undefined }))
                                }}
                                style={{
                                    maxWidth: "100%",
                                    minWidth: 10,
                                }}
                            />
                            <div className="relative h-4 w-4 flex-shrink-0 ml-1.5">
                                {isDisconnected ? (
                                    <div className="absolute">
                                        <Icons.ExtraSmallIcon
                                            Icon={Icons.VideoOff}
                                            className="text-yellow-700"
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className="absolute">
                                            <Icons.ExtraSmallIcon
                                                Icon={Icons.LiveDrone}
                                                className="text-red-500"
                                            />
                                        </div>
                                        {!isActive && (
                                            <div className="absolute">
                                                <Icons.ExtraSmallIcon
                                                    Icon={Icons.LiveDrone}
                                                    className="animate-ping-slow duration-600 text-red-500 opacity-75"
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-between w-full items-center">
                            {entity.createdBy?.userId && (
                                <UserProfileModalTrigger userId={entity.createdBy.userId}>
                                    <div className="flex gap-2 items-center">
                                        <div className="w-4 min-w-[16px] aspect-square rounded overflow-hidden">
                                            <ProfilePhoto
                                                userId={entity.createdBy.userId}
                                                fullName={fullName || ""}
                                            />
                                        </div>
                                        <small className="text-gray-200 group-hover/UserProfileModalTrigger:underline">
                                            {shortName}
                                        </small>
                                    </div>
                                </UserProfileModalTrigger>
                            )}
                            <small className="text-gray-400">{formattedDuration}</small>
                        </div>
                    </div>
                </div>
                <ExpandableContent isOpen={isPreviewOpen && !isActive}>
                    <div className="rounded-lg overflow-hidden mt-2">
                        <VideoFlightPlayer flightId={id} includePiPControls={false} />
                    </div>
                </ExpandableContent>
            </div>
        )
    }
    return LiveVideoStickyListItem
}

export function getLiveVideoInpsectorPanel(spec: PluginSpec<LiveVideoFlight>) {
    const InspectorPanel = getGenericLayerDataInspectorPanelComponent({
        spec,
        LayerComponent: InspectorPanelLiveVideoLayer,
        DataComponent: InspectorPanelLiveVideoData,
    })
    const LiveVideoInspectorPanel = ({
        id,
        plugin,
    }: {
        id?: string
        plugin: InspectorPanelPlugin
    }) => {
        const flight = useAppSelector(spec.selectSingleEntity(id || ""))

        const status = selectLiveVideoStatus(flight)

        if (
            !id ||
            !flight ||
            status === LiveVideoStatus.AwaitingCodeLink ||
            status === LiveVideoStatus.Expired ||
            status === LiveVideoStatus.Standby
        ) {
            return null
        }

        return <InspectorPanel id={id} plugin={plugin} />
    }
    return LiveVideoInspectorPanel
}
